'use client';

import { useEffect, useMemo } from 'react';
import type { WorkspaceClipboard, WorkspaceCommand } from '@/lib/workspace/commands';
import { detectPlatform, matchShortcut } from '@/lib/workspace/commands';
import { useCommandContext } from './use-command-context';
import type { WorkspaceSelection } from './selection-context';

/**
 * Single document-level keydown listener that drives every command
 * registered with `surfaces.shortcut === true`.
 *
 * Mounted once in `workspace-canvas.tsx` (NOT in every node) so the
 * registry stays the canonical place to add a shortcut: extend
 * `lib/workspace/commands.ts` and this hook will pick it up
 * automatically the next render.
 *
 * Focus guard mirrors the existing `paste-handler.tsx` policy:
 * shortcuts NEVER fire while the user is typing into an editable
 * surface (`input`, `textarea`, contenteditable, CodeMirror, etc.).
 * The browser already swallows undo / clipboard shortcuts when the
 * input owns focus, but we also need this guard for our own bindings
 * like Cmd+D so typing a 'd' inside a chat composer doesn't trigger
 * a Duplicate gesture out of nowhere.
 */
export function useWorkspaceShortcuts({
  commands,
  clipboard,
  selectionOverride,
}: {
  commands: WorkspaceCommand[];
  clipboard: WorkspaceClipboard | null;
  /** Pass when mounted above `WorkspaceSelectionProvider`. */
  selectionOverride?: WorkspaceSelection;
}) {
  const platform = useMemo(() => detectPlatform(), []);
  const buildCtx = useCommandContext(clipboard, selectionOverride);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // The shortcut layer should yield to:
      //   - in-flight text composition (IME). `isComposing` is true
      //     while a Japanese / Korean / etc. user is mid-character.
      //   - typing inside any editable surface. We walk up from the
      //     active element so the guard also catches CodeMirror's
      //     deeply-nested contenteditable children.
      if (e.isComposing) return;
      if (isEditableFocus()) return;
      // [COPY-TEXT-01] (2026-10-17) and to a TEXT SELECTION for copy and cut: the owner selected text in an agent's
      // chat window, pressed Cmd+C and pasted "openlane [workspace-clipboard:…]". This layer matched Mod+C to the
      // workspace Copy command (its predicate only asks that a window be selected), called preventDefault, so the
      // browser never copied the text, and the command then wrote the window to the clipboard.
      if (shortcutYieldsToTextSelection(e, hasTextSelection())) return;

      // Resolve commands lazily — building the context every keydown
      // is cheap (no allocations beyond a small object) and means a
      // selection change that happened a few ms ago is reflected
      // immediately without a re-render race.
      const ctx = buildCtx();
      for (const c of commands) {
        if (!c.surfaces.shortcut) continue;
        if (!matchShortcut(c.shortcut, e, platform)) continue;
        if (!c.predicate(ctx)) continue;
        e.preventDefault();
        e.stopPropagation();
        void c.run(ctx);
        return;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [commands, buildCtx, platform]);
}

/**
 * Walk up from the document's active element until we find an
 * editable surface (input / textarea / contenteditable) OR run out
 * of ancestors. Used by every shortcut + paste handler to decide
 * "is the user typing?" without each one re-implementing the walk.
 *
 * Exported so the clipboard handler can share the exact same
 * predicate — keeping the focus-guard semantics in lockstep across
 * surfaces.
 */
export function isEditableFocus(): boolean {
  if (typeof document === 'undefined') return false;
  const ae = document.activeElement as HTMLElement | null;
  if (!ae) return false;
  const tag = ae.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  let n: HTMLElement | null = ae;
  while (n) {
    if (n.isContentEditable) return true;
    n = n.parentElement;
  }
  return false;
}

/** True when the page holds a non-empty text selection (highlighted text in a chat message, a panel, a document). */
export function hasTextSelection(): boolean {
  if (typeof window === 'undefined') return false;
  const sel = window.getSelection();
  return !!sel && !sel.isCollapsed && sel.toString().trim().length > 0;
}

/**
 * [COPY-TEXT-01] Copy and cut (Mod+C, Mod+X, no Alt) belong to the browser while text is selected, so the selected
 * text is what lands on the clipboard; every other shortcut keeps working. Pure, for the unit test.
 */
export function shortcutYieldsToTextSelection(
  e: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'altKey'>,
  textSelected: boolean,
): boolean {
  if (!textSelected || e.altKey || !(e.metaKey || e.ctrlKey)) return false;
  const k = e.key.toLowerCase();
  return k === 'c' || k === 'x';
}
